import classNames from "classnames";
import React from "react";
import { usePopper } from "react-popper";
import { type PopupProps, Portal } from "semantic-ui-react";
import type { InvertResult } from "../../utilities/typeUtils";
import { createReferenceProxy } from "./ReferenceProxy";
// https://github.com/Semantic-Org/Semantic-UI-React/blob/master/src/modules/Popup/lib/positions.js
export const positionsMapping = {
  "top center": "top",
  "top left": "top-start",
  "top right": "top-end",

  "bottom center": "bottom",
  "bottom left": "bottom-start",
  "bottom right": "bottom-end",

  "right center": "right",
  "left center": "left"
} as const;

export const reversedPositionsMapping = Object.keys(positionsMapping).reduce<InvertResult<typeof positionsMapping>>((acc, key) => {
  acc[positionsMapping[key]] = key;
  return acc;

}, {} as any);

interface Props {
  position: PopupProps["position"]
  triggerRef: React.MutableRefObject<undefined>;

  onOpen?: () => Promise<any>
  onClose?: () => void

  react?: {
    open?: boolean
    // Element to render where the tooltip will be positioned
    trigger: React.ReactNode
  }

  jquery?: {
    referenceElement: React.MutableRefObject<HTMLElement>
  }

  width?: number
  style?: React.CSSProperties
  classes: string
}

/**
 * This component is basically one big hack to integrate react tooltips with old jquery code
 * Don't use this for new code
 */
const PopperTooltip = (props: React.PropsWithChildren<Props>) => {
  const [popperElement, setPopperElement] = React.useState<HTMLElement>(null);

  const referenceElement = props.jquery ? props.jquery.referenceElement?.current : createReferenceProxy(props.triggerRef);

  let placement = props.position != null ? positionsMapping[props.position] : undefined;

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      { name: "eventListeners", options: { scroll: false } },
      { name: "preventOverflow", options: { mainAxis: true, altAxis: false } },
      { name: "computeStyles", options: { gpuAcceleration: false } },
    ],
    placement
  });

  // Sometimes popper will decide to ignore our placement due to overflow problems
  if (attributes.popper?.["data-popper-placement"] != null) {
    placement = attributes.popper["data-popper-placement"] as keyof InvertResult<typeof positionsMapping>;
  }

  const parentClasses = classNames(
    "ui popup transition visible",
    reversedPositionsMapping[placement],
    props.classes
  );

  const onClose = (event: React.MouseEvent<HTMLElement>) => {
    // Don't close the menu if the user clicked inside the react select menu
    // The react select menu is rendered in document.body, so clicking in it would otherwise close this tooltip
    const clickedInReactSelectMenu = (event.target as HTMLElement)?.closest?.(".react-select-menu-portal") ?? false;
    const clickedInAnotherPopperPopup = (event.target as HTMLElement)?.closest?.(".popup") ?? false;

    if (!clickedInReactSelectMenu && !clickedInAnotherPopperPopup) { props.onClose?.(); }
  };

  return <Portal
    open={props.react?.open ?? props.jquery?.referenceElement?.current != null}
    onOpen={props.onOpen}
    onClose={onClose}

    closeOnDocumentClick

    trigger={props.react?.trigger ?? <span></span>}
    triggerRef={props.triggerRef}
  >
    <div
      className={parentClasses}
      ref={setPopperElement}
      style={{
        width: props.width ?? undefined,
        maxWidth: "none",
        minWidth: 300,
        minHeight: 100,
        right: "auto",
        left: "auto",
        position: "initial",
        ...styles.popper,
        ...props.style
      }}
      {...attributes.popper}
    >
      {props.children}
    </div>
  </Portal>;
};

export default PopperTooltip;
